import React from "react";
import { motion } from "framer-motion";

const TESTIMONIALS = [
  {
    quote: "I used to spend half a Sunday pulling together numbers before every lender call. Now I just open VaWeNa, hit 'Generate DSCR Report', and I'm done in 60 seconds.",
    name: "Marcus T.",
    role: "7-property landlord · Phoenix, AZ",
    initials: "MT",
    color: "bg-blue-600",
  },
  {
    quote: "The Deal Readiness Score finally gave me a clear answer to the question I'd been asking for months — 'am I actually ready to buy another property?' Turns out, yes. Closed on #4 last month.",
    name: "Priya S.",
    role: "Real estate investor · Austin, TX",
    initials: "PS",
    color: "bg-indigo-600",
  },
  {
    quote: "My CPA told me my Schedule E was the most organized she'd ever received. I didn't have the heart to tell her it took me 2 minutes.",
    name: "James R.",
    role: "Portfolio of 12 units · Nashville, TN",
    initials: "JR",
    color: "bg-emerald-600",
  },
  {
    quote: "I had no idea one of my properties was dragging the whole portfolio down until VaWeNa flagged it. Adjusted rents and refinanced — added $400/mo in cash flow.",
    name: "Angela K.",
    role: "Investor & realtor · Denver, CO",
    initials: "AK",
    color: "bg-cyan-700",
  },
];

const StarRow = () => (
  <div className="flex gap-0.5 mb-3">
    {[...Array(5)].map((_, i) => (
      <svg key={i} className="w-4 h-4 text-yellow-400 fill-current" viewBox="0 0 20 20">
        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
      </svg>
    ))}
  </div>
);

export const TestimonialsSection: React.FC = () => {
  const firstRow = TESTIMONIALS;
  const secondRow = [...TESTIMONIALS].reverse();
  const firstTrack = [...firstRow, ...firstRow];
  const secondTrack = [...secondRow, ...secondRow];

  return (
    <section className="relative w-full px-4 sm:px-6 py-20 overflow-hidden">
      {/* Ambient glow */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[400px] dark:bg-blue-600/5 bg-blue-300/8 rounded-full blur-[100px] pointer-events-none" />

      <div className="max-w-[1200px] mx-auto relative z-10">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-flex px-3 py-1.5 text-xs font-bold tracking-widest dark:text-blue-400 text-blue-700 uppercase dark:bg-blue-600/15 bg-blue-100 rounded-full mb-4">
            Investor Stories
          </div>
          <h2 className="text-3xl sm:text-4xl font-extrabold dark:text-white text-gray-900 mb-3">
            What investors are{" "}
            <span className="bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent">
              saying
            </span>
          </h2>
          <p className="text-base dark:text-slate-400 text-gray-500 max-w-lg mx-auto">
            Real feedback from real estate investors who've replaced their spreadsheets with VaWeNa.
          </p>
        </div>

        {/* Two-line scrolling testimonials */}
        <div className="testimonial-marquee-wrap relative space-y-4 overflow-hidden">
          <div className="pointer-events-none absolute inset-y-0 left-0 z-10 w-12 sm:w-20 bg-gradient-to-r from-blue-50 to-transparent dark:from-[#020D30]" />
          <div className="pointer-events-none absolute inset-y-0 right-0 z-10 w-12 sm:w-20 bg-gradient-to-l from-blue-50 to-transparent dark:from-[#020D30]" />
          <div className="testimonial-marquee-track testimonial-marquee-track--left flex gap-4">
            {firstTrack.map((t, i) => (
              <div
                key={`row1-${t.name}-${i}`}
                className="w-[320px] sm:w-[420px] dark:bg-white/[0.05] bg-white border border-blue-300/80 dark:border-blue-400/30 rounded-2xl p-5 shadow-lg shadow-blue-500/10"
              >
                <StarRow />
                <p className="text-sm dark:text-slate-300 text-gray-700 leading-relaxed mb-4 italic">
                  "{t.quote}"
                </p>
                <div className="flex items-center gap-3">
                  <div className={`w-9 h-9 rounded-full ${t.color} flex items-center justify-center text-xs font-bold text-white flex-shrink-0`}>
                    {t.initials}
                  </div>
                  <div>
                    <div className="text-sm font-semibold dark:text-white text-gray-900">{t.name}</div>
                    <div className="text-xs dark:text-slate-500 text-gray-400">{t.role}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="testimonial-marquee-track testimonial-marquee-track--right flex gap-4">
            {secondTrack.map((t, i) => (
              <div
                key={`row2-${t.name}-${i}`}
                className="w-[320px] sm:w-[420px] dark:bg-white/[0.05] bg-white border border-blue-300/80 dark:border-blue-400/30 rounded-2xl p-5 shadow-lg shadow-blue-500/10"
              >
                <StarRow />
                <p className="text-sm dark:text-slate-300 text-gray-700 leading-relaxed mb-4 italic">
                  "{t.quote}"
                </p>
                <div className="flex items-center gap-3">
                  <div className={`w-9 h-9 rounded-full ${t.color} flex items-center justify-center text-xs font-bold text-white flex-shrink-0`}>
                    {t.initials}
                  </div>
                  <div>
                    <div className="text-sm font-semibold dark:text-white text-gray-900">{t.name}</div>
                    <div className="text-xs dark:text-slate-500 text-gray-400">{t.role}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Bottom social proof strip */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.4 }}
          className="mt-10 text-center"
        >
          <p className="text-sm dark:text-slate-500 text-gray-400">
            Joined by investors managing{" "}
            <span className="dark:text-white text-gray-800 font-semibold">100+ properties</span>{" "}
            across the US · 14-day free trial · No commitment
          </p>
        </motion.div>
      </div>
    </section>
  );
};
